import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';

import {
  clearPendingJoinToken,
  grantedRoleLabel,
  type LinkPreview,
  readPendingJoinToken,
  savePendingJoinToken,
  usePreviewLink,
  useRedeemLink,
} from '@/entities/collab';
import { useViewer } from '@/entities/viewer';
import { FormError } from '@/shared/forms';
import { Button, ErrorState, LoadingState } from '@/shared/ui';

import styles from '@/pages/invitations/InvitationsPage.module.css';

/**
 * `/join#<token>`: an invitation link. The token lives in the URL fragment (never sent to a
 * server or logged) and is kept in session storage across sign-in. Nothing happens until the
 * person presses "Join": opening a link is not consent.
 */
export function JoinPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const preview = usePreviewLink();
  const redeem = useRedeemLink();
  // The fragment wins; otherwise the token saved before sign-in. (No state: derived each render.)
  const fromHash = location.hash.replace(/^#/, '');
  const token = fromHash || readPendingJoinToken();
  const [info, setInfo] = useState<LinkPreview | null>(null);
  const [outcome, setOutcome] = useState<'pending' | null>(null);
  const authenticated = viewer.status === 'authenticated';
  const loading = viewer.status === 'loading';

  // Keep the token only in session storage and take it out of the address bar.
  useEffect(() => {
    if (!fromHash) return;
    savePendingJoinToken(fromHash);
    window.history.replaceState(null, '', window.location.pathname);
  }, [fromHash]);

  useEffect(() => {
    if (!authenticated || token === null || info !== null) return;
    preview.mutate(token, {
      onSuccess: setInfo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, token]);

  if (loading) return <LoadingState />;
  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: '/join' }} />;
  }
  if (token === null) {
    return (
      <div className={styles.page}>
        <ErrorState error={null} />
        <Link to="/app">{t('common.backHome')}</Link>
      </div>
    );
  }
  if (preview.isError) {
    return (
      <div className={styles.page}>
        <h1>{t('join.title')}</h1>
        <FormError error={preview.error} />
        <Link
          to="/app"
          onClick={() => {
            clearPendingJoinToken();
          }}
        >
          {t('common.backHome')}
        </Link>
      </div>
    );
  }
  if (!info) return <LoadingState />;

  return (
    <div className={styles.page}>
      <h1>{t('join.title')}</h1>
      {outcome === 'pending' ? (
        <p role="status">{t('join.pending')}</p>
      ) : info.already_participant ? (
        <p role="status">{t('join.already', { name: info.target_name })}</p>
      ) : info.rejected ? (
        // Rejected through THIS link before: the request cannot be repeated (another link or a
        // personal invitation is a separate decision).
        <p role="alert">{t('errors.INVITE_LINK_REJECTED')}</p>
      ) : (
        <>
          <p>
            {t(info.scope === 'workspace' ? 'join.toWorkspace' : 'join.toBoard', {
              name: info.target_name,
            })}
          </p>
          <p className={styles.meta}>
            {t('join.role', { role: grantedRoleLabel(t, info.role, info.custom_role_name) })}
            {info.requires_approval && ` · ${t('join.needsApproval')}`}
          </p>
          <FormError error={redeem.error} />
          <div className={styles.actions}>
            <Button
              variant="primary"
              disabled={redeem.isPending}
              onClick={() => {
                redeem.mutate(token, {
                  onSuccess: (result) => {
                    clearPendingJoinToken();
                    if (result.outcome === 'pending_approval') {
                      setOutcome('pending');
                      return;
                    }
                    if (result.board_id) void navigate(`/app/boards/${result.board_id}`);
                    else if (result.workspace_id) {
                      void navigate(`/app/workspaces/${result.workspace_id}`);
                    } else void navigate('/app');
                  },
                });
              }}
            >
              {t('join.join')}
            </Button>
            <Button
              onClick={() => {
                clearPendingJoinToken();
                void navigate('/app');
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </>
      )}
      <Link
        to="/app"
        onClick={() => {
          clearPendingJoinToken();
        }}
      >
        {t('common.backHome')}
      </Link>
    </div>
  );
}
